import { kyRequest } from './kyHttp';

const BASE_URL = 'events';
const TENANT_ID = process.env.REACT_APP_TENANT_TITLE;

/**
 * Get detailed information about a specific event by slug
 * @param {string} slug - The event slug
 * @returns {Promise<Object>} - Response with payload, error, and success status
 * @example
 * // Response format:
 * // {
 * //   payload: {
 * //     // Event details
 * //   },
 * //   error: null,
 * //   success: true
 * // }
 */
const getEventDetailData = (slug) => {
  return kyRequest
    .get(`tenant/${TENANT_ID}/${BASE_URL}/${slug}`)
    .json()
    .then((res) => {
      const payload = {
        success: res.success,
        data: res.data,
      };
      return { payload, error: null, success: true };
    })
    .catch((error) => ({ payload: null, error: error.response, success: false }));
};

/**
 * Get all event categories, kinds, and types
 * @returns {Promise<Object>} - Response with payload, error, and success status
 * @example
 * // Response format:
 * // {
 * //   payload: {
 * //     categories: [
 * //       { id: 1, title: "Product Development" },
 * //       { id: 5, title: "Marketing" },
 * //       // ...more categories
 * //     ],
 * //     kinds: [
 * //       { id: 1, title: "Live Class" },
 * //       { id: 2, title: "Bootcamp" }
 * //     ],
 * //     types: [
 * //       { id: "online", title: "Online" },
 * //       { id: "offline", title: "Offline" },
 * //       { id: "hybrid", title: "Hybrid" }
 * //     ]
 * //   },
 * //   error: null,
 * //   success: true
 * // }
 */
const getEventCategories = () => {
  return kyRequest
    .get(`tenant/${TENANT_ID}/${BASE_URL}/categories`)
    .json()
    .then((res) => ({ payload: res.payload, error: null, success: true }))
    .catch((error) => ({ payload: null, error: error.response, success: false }));
};

/**
 * Get list of events with optional filters
 * @param {Object} filters - Optional filters for events
 * @param {number} filters.category - Filter by category ID
 * @param {number} filters.event_kind - Filter by event kind ID
 * @param {string} filters.event_type - Filter by event type (online/offline/hybrid)
 * @param {number} filters.min_price - Filter by minimum price
 * @param {number} filters.max_price - Filter by maximum price
 * @param {string} filters.query - Search text in event title
 * @param {number} filters.limit - Number of results per page
 * @returns {Promise<Object>} - Response with payload, error, and success status
 * @example
 * // Response format:
 * // {
 * //   payload: {
 * //     data: [
 * //       {
 * //         uuid: "9c5cce41-68b3-4fee-9237-553d52219b78",
 * //         slug: "umroh",
 * //         title: "Umroh",
 * //         type: 1,
 * //         event_type: "offline",
 * //         event_kind: "Live Class",
 * //         early_ticket_price: 0,
 * //         early_ticket_date: null,
 * //         ticket_price: 1000000,
 * //         discount: "5.00",
 * //         session_start: null,
 * //         session_end: null,
 * //         session_formatted: "TBA",
 * //         location: "Jalan Arif Rahman Hakim, Kemirimuka, Beji, Depok, Jawa Barat, Jawa, 16422, Indonesia",
 * //         image_url: "https://storage.agrego.id/elearning/events/9c5cce41-68b3-4fee-9237-553d52219b78/1740464679-thumbnail.png",
 * //         favourites: false
 * //       },
 * //       // ...more events
 * //     ],
 * //     pagination: {
 * //       total: 15,
 * //       per_page: 10,
 * //       current_page: 1,
 * //       last_page: 2,
 * //       from: 1,
 * //       to: 10
 * //     }
 * //   },
 * //   error: null,
 * //   success: true
 * // }
 */
const getEventsList = (filters = {}) => {
  return kyRequest
    .get(`tenant/${TENANT_ID}/${BASE_URL}`, { searchParams: filters })
    .json()
    .then((res) => ({
      payload: {
        data: res.data,
        pagination: res.pagination,
      },
      error: null,
      success: res.success || true,
    }))
    .catch((error) => ({ payload: null, error: error.response, success: false }));
};

/**
 * Get events filtered by category with additional filters
 * @param {Object} filters - Optional filters for events
 * @param {number} filters.category - Filter by category ID
 * @param {number} filters.event_kind - Filter by event kind ID
 * @param {string} filters.event_type - Filter by event type (online/offline/hybrid)
 * @param {number} filters.min_price - Filter by minimum price
 * @param {number} filters.max_price - Filter by maximum price
 * @param {string} filters.query - Search text in event title
 * @param {number} filters.limit - Number of results per page
 * @returns {Promise<Object>} - Response with payload, error, and success status
 * @example
 * // Response format:
 * // {
 * //   payload: {
 * //     data: [
 * //       {
 * //         uuid: "9c5cce41-68b3-4fee-9237-553d52219b78",
 * //         slug: "umroh",
 * //         title: "Umroh",
 * //         type: 1,
 * //         event_type: "offline",
 * //         event_kind: "Live Class",
 * //         early_ticket_price: 0,
 * //         early_ticket_date: null,
 * //         ticket_price: 1000000,
 * //         discount: "5.00",
 * //         session_start: null,
 * //         session_end: null,
 * //         session_formatted: "TBA",
 * //         location: "Jalan Arif Rahman Hakim, Kemirimuka, Beji, Depok, Jawa Barat, Jawa, 16422, Indonesia",
 * //         image_url: "https://storage.agrego.id/elearning/events/9c5cce41-68b3-4fee-9237-553d52219b78/1740464679-thumbnail.png",
 * //         favourites: false
 * //       },
 * //     ],
 * //     pagination: {
 * //       total: 15,
 * //       per_page: 10,
 * //       current_page: 1,
 * //       last_page: 2,
 * //       from: 1,
 * //       to: 10
 * //     }
 * //   },
 * //   error: null,
 * //   success: true
 * // }
 */
const getEventsByCategory = (filters = {}) => {
  return kyRequest
    .get(`${BASE_URL}/by-category`, { searchParams: filters })
    .json()
    .then((res) => ({
      payload: {
        data: res.data,
        pagination: res.pagination,
      },
      error: null,
      success: true,
    }))
    .catch((error) => ({ payload: null, error: error.response, success: false }));
};

/**
 * Get ticket information by UID
 * @param {string} uid - Ticket unique identifier
 * @returns {Promise<Object>} - Response with payload, error, and success status
 * @example
 * // Response format:
 * // {
 * //   payload: {
 * //     // Ticket details
 * //   },
 * //   error: null,
 * //   success: true
 * // }
 */
const getTicket = (uid) => {
  return kyRequest
    .get(`ticket/${uid}`)
    .json()
    .then((res) => ({ payload: res.payload, error: null, success: true }))
    .catch((error) => ({ payload: null, error: error.response, success: false }));
};

export const SERVICE_EVENT = {
  getEventDetailData,
  getTicket,
  getEventCategories,
  getEventsList,
  getEventsByCategory,
};
